import React, { useRef, useCallback, useState, useEffect } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import Webcam from 'react-webcam';
import { FaTimes, FaCamera, FaBarcode } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { BrowserMultiFormatReader } from '@zxing/browser';

import {
  ModalOverlay,
  ModalContainer,
  Container,
  Header,
  CameraContainer,
  Controls,
  CloseButton,
  CaptureButton,
} from './styles';

const SimpleCameraModal = ({
  isOpen,
  setIsOpen,
  onBarcodeDetected,
  onPhotoTaken,
}) => {
  const webcamRef = useRef(null);
  const codeReaderRef = useRef(null);
  const [facingMode, setFacingMode] = useState('environment'); // Sempre começar com câmera traseira
  const [isScanning, setIsScanning] = useState(false);
  const [scanningInterval, setScanningInterval] = useState(null);
  const [torch, setTorch] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [scanCount, setScanCount] = useState(0);
  const [cameraError, setCameraError] = useState(null);

  const handleClose = useCallback(() => {
    // Parar o scanning se estiver ativo
    if (scanningInterval) {
      clearInterval(scanningInterval);
      setScanningInterval(null);
    }
    setIsScanning(false);
    setScanCount(0); // Reset do contador
    setZoom(1.0); // Reset do zoom
    setTorch(false); // Reset da lanterna
    setIsOpen(false);
  }, [setIsOpen, scanningInterval]);

  // Verificar dispositivos de câmera disponíveis - sempre usar câmera traseira
  useEffect(() => {
    if (isOpen) {
      // Sempre forçar o uso da câmera traseira
      setFacingMode('environment');
    }
  }, [isOpen]);

  // Função para iniciar/parar o scanning de código de barras
  const toggleBarcodeScan = useCallback(() => {
    if (isScanning) {
      // Parar scanning
      if (scanningInterval) {
        clearInterval(scanningInterval);
        setScanningInterval(null);
      }
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.reset();
        } catch (e) {
          // ignore
        }
        codeReaderRef.current = null;
      }
      setIsScanning(false);
    } else {
      // Iniciar scanning
      setIsScanning(true);
      setScanCount(0); // Reset do contador ao iniciar

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;
      // Configurar hints para melhor performance
      const hints = new Map();
      hints.set('TRY_HARDER', true);
      hints.set('POSSIBLE_FORMATS', [
        'CODE_128',
        'CODE_39',
        'CODE_93',
        'CODABAR',
        'ITF',
        'EAN_13',
        'EAN_8',
        'UPC_A',
        'UPC_E',
      ]);
      hints.set('CHARACTER_SET', 'UTF-8');

      codeReader.hints = hints;

      const interval = setInterval(async () => {
        setScanCount(prev => prev + 1);

        if (webcamRef.current && webcamRef.current.video) {
          try {
            const { video } = webcamRef.current;

            if (video.readyState !== video.HAVE_ENOUGH_DATA) {
              return;
            }

            if (video.videoWidth === 0 || video.videoHeight === 0) {
              return;
            }

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            context.filter = 'contrast(1.2) brightness(1.1) saturate(0.8)';
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const regions = [
              { x: 0, y: 0, width: canvas.width, height: canvas.height },
              {
                x: canvas.width * 0.1,
                y: canvas.height * 0.3,
                width: canvas.width * 0.8,
                height: canvas.height * 0.4,
              },
              // Região superior central
              {
                x: canvas.width * 0.1,
                y: canvas.height * 0.1,
                width: canvas.width * 0.8,
                height: canvas.height * 0.3,
              },
              // Região inferior central
              {
                x: canvas.width * 0.1,
                y: canvas.height * 0.6,
                width: canvas.width * 0.8,
                height: canvas.height * 0.3,
              },
            ];

            const processRegions = async () => {
              // eslint-disable-next-line no-restricted-syntax
              for (const region of regions) {
                try {
                  const regionCanvas = document.createElement('canvas');
                  const regionContext = regionCanvas.getContext('2d');

                  regionCanvas.width = region.width;
                  regionCanvas.height = region.height;

                  regionContext.drawImage(
                    canvas,
                    region.x,
                    region.y,
                    region.width,
                    region.height,
                    0,
                    0,
                    region.width,
                    region.height
                  );

                  // eslint-disable-next-line no-await-in-loop
                  const result = await codeReader.decodeFromCanvas(
                    regionCanvas
                  );

                  if (result && result.text) {
                    clearInterval(interval);
                    setScanningInterval(null);
                    setIsScanning(false);

                    if (onBarcodeDetected) {
                      onBarcodeDetected(result.text);
                    }

                    // Fechar o modal após detectar o código
                    setIsOpen(false);
                    if (codeReaderRef.current) {
                      try {
                        codeReaderRef.current.reset();
                      } catch (e) {
                        // ignore
                      }
                      codeReaderRef.current = null;
                    }
                    return true;
                  }
                } catch (regionError) {
                  // Continuar tentando outras regiões
                }
              }
              return false;
            };

            await processRegions();
          } catch (error) {
            // Erro durante scanning - continuar tentando
          }
        }
      }, 200); // Reduzir intervalo para 200ms para ser mais responsivo

      setScanningInterval(interval);
    }
  }, [isScanning, scanningInterval, onBarcodeDetected, setIsOpen]);

  // Função para capturar foto e retornar base64
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      if (onPhotoTaken) {
        onPhotoTaken(imageSrc);
      }
      setIsOpen(false);
    }
  }, [onPhotoTaken, setIsOpen]);

  // Montar constraints de vídeo de forma compatível com Android/Chrome
  // Muitos dispositivos Android não suportam constraints não-padronizadas
  // (torch, zoom, advanced, focusMode etc.) e isso pode causar
  // OverconstrainedError / NotReadableError. Usar constraints mínimas
  // que funcionam na maioria dos navegadores/mobile.
  const videoConstraints = (() => {
    const base = {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: { ideal: facingMode || 'environment' },
    };

    // Se o userAgent indicar Android, manter constraints bem simples
    try {
      const ua = navigator.userAgent || '';
      if (/Android/i.test(ua)) {
        return base;
      }
    } catch (e) {
      // ignore
    }

    // Para outros dispositivos (ex: iOS desktop) podemos deixar um pouco
    // mais permissivo, mas sem usar constraints não padronizadas.
    return base;
  })();

  // Forçar atributos no elemento <video> para permitir autoplay em mobile
  useEffect(() => {
    try {
      const videoEl = webcamRef.current && webcamRef.current.video;
      if (videoEl) {
        videoEl.playsInline = true; // iOS/Android inline
        videoEl.muted = true; // autoplay geralmente requer muted
        // Tentar iniciar o play para ativar stream automaticamente
        const playPromise = videoEl.play && videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {
            // ignore auto-play rejection, user will need to interact
          });
        }
      }
    } catch (err) {
      // não bloquear
    }
  }, [isOpen, facingMode]);

  // Handler de erro quando getUserMedia falha (permissão negada, constraints)
  const handleUserMediaError = useCallback(error => {
    // Expor mensagem curta para UI e log detalhado para console
    let msg = 'Erro ao acessar a câmera.';
    if (error && error.name) {
      if (
        error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError'
      ) {
        msg =
          'Permissão negada para acessar a câmera. Verifique as permissões do navegador.';
      } else if (
        error.name === 'NotFoundError' ||
        error.name === 'DevicesNotFoundError'
      ) {
        msg = 'Nenhuma câmera encontrada neste dispositivo.';
      } else if (error.name === 'OverconstrainedError') {
        msg =
          'Configuração de câmera não suportada pelo dispositivo (constraints).';
      }
    }
    setCameraError(msg);
    // manter console.log para debug
    // eslint-disable-next-line no-console
    console.error('getUserMedia error:', error);
  }, []);

  // Cleanup global: garantir clearing de intervalos e reset do codeReader
  useEffect(() => {
    return () => {
      if (scanningInterval) {
        clearInterval(scanningInterval);
      }
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.reset();
        } catch (e) {
          // ignore
        }
        codeReaderRef.current = null;
      }
    };
  }, [scanningInterval]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <Container>
          <Header>
            <h2>Câmera</h2>
            <CloseButton onClick={handleClose}>
              <FaTimes />
            </CloseButton>
          </Header>

          <CameraContainer>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              onUserMediaError={handleUserMediaError}
              onUserMedia={() => setCameraError(null)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {/* Mostrar erro de câmera quando houver */}
            {cameraError && (
              <div
                style={{
                  position: 'absolute',
                  left: '10%',
                  right: '10%',
                  top: '10%',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                {cameraError}
              </div>
            )}
            {/* Só mostra overlay de scan se onBarcodeDetected estiver presente */}
            {onBarcodeDetected && isScanning && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '3px solid #00ff00',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 255, 0, 0.1)',
                }}
              >
                <div
                  style={{
                    color: '#00ff00',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    marginBottom: '20px',
                  }}
                >
                  Escaneando código de barras...
                </div>
                <div
                  style={{
                    width: '200px',
                    height: '100px',
                    border: '2px solid #00ff00',
                    borderRadius: '5px',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '0',
                      right: '0',
                      height: '2px',
                      backgroundColor: '#ff0000',
                      animation: 'scan 2s linear infinite',
                    }}
                  />
                </div>
              </div>
            )}
          </CameraContainer>

          <Controls>
            {/* Controles adicionais para melhorar a leitura */}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setFacingMode(prev =>
                    prev === 'environment' ? 'user' : 'environment'
                  )
                }
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  background: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                🔄 Trocar Câmera
              </button>

              <button
                type="button"
                onClick={() => setTorch(prev => !prev)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  background: torch ? '#ffeb3b' : '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                🔦 {torch ? 'Desligar' : 'Ligar'} Flash
              </button>

              <button
                type="button"
                onClick={() =>
                  setZoom(prev => (prev >= 3.0 ? 1.0 : prev + 0.5))
                }
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ccc',
                  background: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                🔍 Zoom {zoom.toFixed(1)}x
              </button>
            </div>

            {/* Status do scanning */}
            {onBarcodeDetected && isScanning && (
              <div
                style={{
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: '#666',
                  textAlign: 'center',
                }}
              >
                Tentativas: {scanCount} | Posicione o código de barras na área
                central
              </div>
            )}

            {/* Só mostra botão de scan se onBarcodeDetected estiver presente */}
            {onBarcodeDetected ? (
              <CaptureButton
                onClick={toggleBarcodeScan}
                className={isScanning ? 'scanning' : 'barcode'}
              >
                <FaBarcode />
                {isScanning ? 'Parar Scan' : 'Ler Código'}
              </CaptureButton>
            ) : (
              <CaptureButton onClick={capture}>
                <FaCamera />
                Capturar Foto
              </CaptureButton>
            )}
          </Controls>
        </Container>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default SimpleCameraModal;
